import { useState } from 'react'
import { toast } from 'sonner'

import { AdminLayout } from '../components/AdminLayout.js'
import { useDevReset, useWalletVerify } from '../hooks/useSystem.js'
import { AdminApiError } from '../adminApi.js'

/**
 * System-level utilities. For slice 3c this is just the Google Wallet config
 * verification — it writes a DRAFT LoyaltyClass against the configured issuer
 * and reports whether the credentials are live. Future system tools land here
 * too.
 *
 * Note: this check only confirms the issuer is *writable* (a draft insert
 * succeeds). It does not — and cannot — report whether publishing access is
 * granted; that's account-level state Google doesn't expose in an insert
 * response. Production publishing access was approved 2026-05-15 (see
 * CLAUDE.md "Google Wallet status").
 */
export function System() {
  const verify = useWalletVerify()
  const result = verify.data

  return (
    <AdminLayout>
      <div className="admin-page-head">
        <h1>System</h1>
      </div>

      <div className="admin-section">
        <h3>Google Wallet configuration</h3>
        <div className="admin-card">
          <p style={{ marginTop: 0 }}>
            Runs a live check against the configured Google Wallet issuer by
            creating a draft loyalty class. Use this after changing wallet
            credentials or to confirm the issuer is still writable. It checks
            write access only — not publishing access.
          </p>
          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              onClick={() => verify.mutate()}
              disabled={verify.isPending}
            >
              {verify.isPending ? 'Verifying…' : 'Verify wallet config'}
            </button>
          </div>

          {verify.isError && (
            <div className="admin-inline-error" style={{ marginTop: 16 }}>
              The verification request failed to complete. Check the API logs.
            </div>
          )}

          {result && result.ok && (
            <div style={{ marginTop: 16 }}>
              <span className="admin-badge admin-badge-ok">Passed</span>
              <dl className="admin-dl" style={{ marginTop: 12 }}>
                <dt>HTTP status</dt>
                <dd>{result.status}</dd>
                <dt>Class created</dt>
                <dd>{result.class_id}</dd>
              </dl>
              <p className="admin-hint">
                The issuer is writable. This confirms credentials only — it
                doesn't report publishing-access status (granted 2026-05-15).
              </p>
            </div>
          )}

          {result && !result.ok && (
            <div style={{ marginTop: 16 }}>
              <span className="admin-badge admin-badge-danger">Failed</span>
              <dl className="admin-dl" style={{ marginTop: 12 }}>
                <dt>Stage</dt>
                <dd>{result.stage}</dd>
                <dt>HTTP status</dt>
                <dd>{result.status ?? '—'}</dd>
              </dl>
              <pre className="admin-json">
                {typeof result.error === 'string'
                  ? result.error
                  : JSON.stringify(result.error, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>

      <DevResetPanel />
    </AdminLayout>
  )
}

/**
 * Staging-only nuclear reset. The corresponding API route is mounted only
 * when NODE_ENV !== 'production', so clicking this in prod hits a 404 and
 * the error surfaces. UI-side we also require the admin to type NUKE to
 * arm the button — two real barriers.
 */
function DevResetPanel() {
  const reset = useDevReset()
  const [typed, setTyped] = useState('')
  const armed = typed === 'NUKE'
  const result = reset.data

  function run() {
    if (!armed) return
    reset.mutate(undefined, {
      onSuccess: () => {
        toast.success('Staging wiped. Tenants, customers, cards all gone.')
        setTyped('')
      },
      onError: (err) => {
        const msg =
          err instanceof AdminApiError
            ? err.message
            : 'Reset failed — this endpoint may not be enabled on prod.'
        toast.error(msg)
      },
    })
  }

  return (
    <div className="admin-section">
      <h3 style={{ color: 'var(--admin-danger, #c8553d)' }}>
        Danger zone — staging only
      </h3>
      <div className="admin-card">
        <p style={{ marginTop: 0 }}>
          Wipe every tenant, customer, card, stamp, and audit row. Preserves
          your superadmin login and the plan-tier catalog. Use this to reset
          staging to empty when you've polluted it with test data.
        </p>
        <p
          className="admin-hint"
          style={{ marginBottom: 12, color: 'var(--admin-danger, #c8553d)' }}
        >
          Only mounted when the server is booted with{' '}
          <code>ALLOW_DEV_RESET=1</code> (staging). Prod never sets it, so
          the route doesn't exist there. If you see a 404 here, the env var
          isn't on for this environment — by design, not a bug.
        </p>
        <label
          htmlFor="dev-reset-confirm"
          style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}
        >
          Type <code>NUKE</code> to enable the button
        </label>
        <input
          id="dev-reset-confirm"
          className="admin-input"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="NUKE"
          autoComplete="off"
          style={{ maxWidth: 260 }}
        />
        <div className="admin-form-actions" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="admin-btn admin-btn-danger"
            onClick={run}
            disabled={!armed || reset.isPending}
          >
            {reset.isPending ? 'Wiping…' : 'Wipe staging data'}
          </button>
        </div>

        {result && (
          <div style={{ marginTop: 16 }}>
            <span className="admin-badge admin-badge-ok">Done</span>
            <dl className="admin-dl" style={{ marginTop: 12 }}>
              <dt>Truncated</dt>
              <dd>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {result.truncated.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </dd>
              <dt>Preserved</dt>
              <dd>{result.preserved.join(', ')}</dd>
            </dl>
          </div>
        )}
      </div>
    </div>
  )
}
